import { BanknotesIcon, ChatBubbleLeftRightIcon, DocumentTextIcon, GlobeAltIcon } from "@heroicons/react/24/outline";
import { getUserInfo } from "../utils/auth";
import { claimDisplayStatus } from "../config/claimStatus";
import { useState } from "react";
import { Card, Container, EmptyState, Loading, MetricCard, SectionTitle, Select } from "./ui";
import { formatCurrency } from "./selection/settingsColumns";
import BarChart from "./dashboard/BarChart";
import DonutChart from "./dashboard/DonutChart";
import GenderSplit from "./dashboard/GenderSplit";
import { NEUTRAL, SERIES, STATUS } from "./dashboard/chartColors";
import { useBursarySummary, useYearScholarships } from "./dashboard/useDashboardSummary";
import { useAcademicYears, useCurrentAcademicYear } from "../hooks/useAcademicYears";

// Each status's label and colour, in the order the charts list them
const LOAN_STATUSES = {
  PENDING: { label: "Pending", color: STATUS.warning },
  GRANTED: { label: "Granted", color: STATUS.good },
  REJECTED: { label: "Rejected", color: STATUS.critical },
};
const CLAIM_STATUSES = {
  PENDING: { label: "Pending", color: STATUS.warning },
  APPROVED: { label: "Approved", color: STATUS.good },
  REJECTED: { label: "Rejected", color: STATUS.critical },
};

/*
 * Donut items for [{ key, total }] counts: every known status (zero when absent), then any other.
 * `normalise` folds keys that mean the same status, like a claim with no status and a RECEIVED one.
 */
const statusItems = (counts = [], statuses, normalise = (key) => key ?? "PENDING") => {
  const byStatus = new Map(Object.keys(statuses).map((key) => [key, 0]));
  counts.forEach(({ key, total }) => {
    const status = normalise(key);
    byStatus.set(status, (byStatus.get(status) ?? 0) + total);
  });
  return [...byStatus].map(([key, value]) => ({ key, value, label: statuses[key]?.label ?? key, color: statuses[key]?.color ?? NEUTRAL }));
};

function SelectionFigure({ label, value }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-sm text-slate-500">{label}</span>
      <span className="text-xl font-semibold text-slate-900">{value}</span>
    </div>
  );
}

// Live figures for one academic year: scholarships from the dashboard summary, bursaries and claims
// from the Loans service; the current year, as Settings define it, unless another is chosen
function YearFigures() {
  const [chosen, setChosen] = useState("");
  const { data: current, isPending: currentPending } = useCurrentAcademicYear();
  const { data: years = [], isPending: yearsPending } = useAcademicYears();
  const year = chosen || current?.academicYearCode || years[0]?.academicYearCode;
  const yearId = years.find((y) => y.academicYearCode === year)?.id ?? (current && current.academicYearCode === year ? current.id : undefined);
  const { data: summary, isPending, isError, refetch } = useYearScholarships(yearId);
  const bursary = useBursarySummary(yearId);

  if (!year) {
    if (currentPending || yearsPending) return <Loading label="Loading this year's figures…" />;
    return <EmptyState title="No academic year yet" description="Create an academic year to see its figures here." />;
  }
  if (isPending || bursary.isPending) return <Loading label="Loading this year's figures…" />;
  if (isError || !summary || bursary.isError) {
    return (
      <EmptyState
        title="Failed to load this year's figures"
        description="Please try again."
        onRefresh={() => {
          refetch();
          bursary.refetch();
        }}
      />
    );
  }

  const scholarships = summary.windows;
  const yearName = years.find((y) => y.academicYearCode === year)?.academicYearName ?? current?.academicYearName ?? year;
  const { selection } = bursary.data;
  const loanItems = statusItems(bursary.data.requestsByStatus, LOAN_STATUSES);
  const claims = statusItems(bursary.data.resultClaimsByStatus, CLAIM_STATUSES, claimDisplayStatus);
  const grantedLoans = loanItems.find((i) => i.key === "GRANTED").value;
  const pendingClaims = claims.find((i) => i.key === "PENDING").value;

  return (
    <section className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <SectionTitle title={`Academic year ${yearName}`} className="w-auto" />
        <Select
          aria-label="Academic year"
          value={year}
          onChange={(e) => setChosen(e.target.value)}
          options={years.map((y) => ({ value: y.academicYearCode, label: y.academicYearName }))}
          className="w-48"
        />
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard title="Open scholarships" value={`${scholarships.open} of ${scholarships.total}`} icon={<GlobeAltIcon className="h-4 w-4" />} />
        <MetricCard title="Scholarship applications" value={summary.submitted} icon={<DocumentTextIcon className="h-4 w-4" />} />
        <MetricCard title="Granted local loans" value={grantedLoans} icon={<BanknotesIcon className="h-4 w-4" />} />
        <MetricCard title="Pending claims" value={pendingClaims} icon={<ChatBubbleLeftRightIcon className="h-4 w-4" />} />
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <Card title="Scholarship applications" subtitle="By stage">
          <BarChart items={summary.stages} />
        </Card>
        <Card title="Scholarships and abroad loans" subtitle="By application window">
          <DonutChart
            totalLabel="Published"
            items={[
              { key: "open", label: "Open", value: scholarships.open, color: SERIES.blue },
              { key: "upcoming", label: "Not yet open", value: scholarships.upcoming, color: SERIES.yellow },
              { key: "closed", label: "Closed", value: scholarships.closed, color: NEUTRAL },
            ]}
          />
        </Card>
        <Card title="Local loans" subtitle="By status">
          <DonutChart items={loanItems} totalLabel="Local loans" />
        </Card>
        <Card title="Student claims" subtitle="By status">
          <DonutChart items={claims} totalLabel="Claims" />
        </Card>
        <Card title="Granted local loans" subtitle="By gender and study area">
          <GenderSplit counts={bursary.data.grantedByGenderAndStudyArea} />
        </Card>
        <Card title="Admitted students" subtitle="By gender and area">
          <GenderSplit counts={bursary.data.admittedByGenderAndArea} />
        </Card>
        <Card title="Selection plan" subtitle="Summed over every institution and study area" className="lg:col-span-2">
          {!selection.selectedStudents && !selection.totalTuitionFees && !selection.totalLivingAllowance ? (
            <p className="text-sm text-slate-500">No selection plan set for this year</p>
          ) : (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
              <SelectionFigure label="Students to select" value={selection.selectedStudents.toLocaleString("en-US")} />
              <SelectionFigure label="Total tuition fees" value={formatCurrency(selection.totalTuitionFees)} />
              <SelectionFigure label="Total living allowance" value={formatCurrency(selection.totalLivingAllowance)} />
            </div>
          )}
        </Card>
      </div>
    </section>
  );
}

// Admin landing page: a welcome, then this academic year's figures
export default function Dashboard() {
  const userName = getUserInfo()?.firstName || "User";

  return (
    <Container className="flex flex-col gap-8 py-6">
      <section className="flex flex-col gap-3 rounded-lg bg-brand p-6 text-white shadow-sm md:p-8">
        <h1 className="text-2xl font-bold md:text-3xl">
          Welcome back, <span className="text-brand-accent">{userName}</span>
        </h1>
        <p className="max-w-2xl text-white/80">Higher Education Council - Rwanda's Gateway to International Excellence</p>
      </section>

      <YearFigures />
    </Container>
  );
}
